import { connect } from "react-redux";
import LoadingBar from "react-top-loading-bar";
import React, { Fragment, useEffect, useRef } from "react";

import { handlePubSubKeys } from "../../../system";
import IdleUserModal from "../../../shared/hooks/IdleUserModal";
import {
  toastType,
  shopifyConstants,
  storeConstants,
  inventoryConstants,
  EMPTY_STRING,
  globalConstants,
  transactionConstants,
} from "../../../constants";
import { useWebPubSub } from "../../../shared/hooks/useWebPubSub";
import {
  shopifyActions,
  storeActions,
  transactionActions,
} from "../../../redux/actions";
import DraftConfirmationModal from "../../../views/SellAndTrade/Shared/DraftConfirmationModal";
import ToastPrintLabelModal from "../../../shared/components/ToastPrintLabel/ToastPrintLabelModal";
import ViewOrderNotification from "../../../shared/components/ToastPrintLabel/ViewOrderNotification";
import OrderDetailsModal from "../../../views/SellAndTrade/Orders/OrderDetailsModal/OrderDetailsModal";
import {
  customToast,
  handleMarkOrderAsOpen,
  handleMarkOrderAsReady,
  handleMarkOrderAsFulfilled,
  handleTransferToLocation,
  getSumOfPaymentHistory,
} from "../../../shared/utility";
import { success } from "../../../redux/actions/actionsUtilities";

const MainWrapper = ({
  children,
  currentStore,
  spinnerArray,
  auditCompleted,
  isAuthenticated,
  setCurrentStore,
  updateAuditData,
  isPrintLabelModal,
  getUserNotifications,
  isDraftConfirmationModal,
  updateShopifyOrderStatus,
  resetShopifyStateToEmpty,
  isShowShopifyOrderNotificationModal,
  shopifyOrderNotificationModalDetails,
  setIsShowShopifyOrderNotificationModal,
  setTransactionData,
  transaction,
}) => {
  const loadingBarRef = useRef(null);
  const prevLengthRef = useRef(spinnerArray.length);
  const { transactionData } = transaction;
  // const { reset } = useIdleUserClose(10000);

  // Hook: Refresh when backend sends REFRESH_DATA
  useWebPubSub(currentStore, (keys) => {
    handlePubSubKeys(keys, {
      onNewShopifyOrder: (key) => {
        getUserNotifications(currentStore?.id);
        customToast(
          <span className="d-flex flex-column gap-2">
            {key.message.message}
            {<ViewOrderNotification orderId={key.message.orderId} />}
          </span>,
          toastType.INFO,
          7000
        );
      },
      onCustomerSyncWithShopifyUpdate: (key) => {
        setCurrentStore(key.message.body.data);
        customToast(key.message.body.message, toastType.INFO, 7000);
      },
      onShopifyIntegrationDisable: (key) => {
        if (currentStore?.id === key.message.body.data.id) {
          setCurrentStore(key.message.body.data);
          resetShopifyStateToEmpty();
        }
        customToast(key.message.body.message, toastType.INFO, 7000);
      },
      onAuditInitialized: (key) => {
        if (currentStore?.id === key.message.body.data.store.id) {
          updateAuditData(key.message.body.data);
        }
        customToast(key.message.body.message, toastType.INFO, 7000);
      },
      onAuditCompleted: (key) => {
        if (currentStore?.id === key.message.body.data.id) {
          auditCompleted(key.message.body.data.id);
        }
        customToast(key.message.body.message, toastType.INFO, 7000);
      },
      onSlingshotPaymentStatus: (key) => {
        console.log("Slingshot Payment Status Key:", key);
        const { data: transactionObject, message } = key.message.body;
        const { status } = key.message.body.data;
        const isCurrentStoreTransaction =
          transactionObject?.store?.id === currentStore?.id;

        if (!isCurrentStoreTransaction) return;

        const isApproved = status?.toLowerCase() === "approved";
        if (isApproved) {
          const isPaymentComplete =
            transactionObject?.TransactionType !== transactionConstants.DRAFT;
          const remainingAmount = Number(
            transactionObject.totalAmountToPay -
              getSumOfPaymentHistory(transactionObject.paymentHistory)
          ).toFixed(2);

          const updatedTransactionData = {
            ...transactionObject,
            store: transactionObject.store,
            paymentMethod: isPaymentComplete
              ? transactionConstants.PAYMENT_COMPLETE
              : EMPTY_STRING,
            currentAmountToPay: remainingAmount,
            poiId: EMPTY_STRING,
            giftCard: globalConstants.EMPTY_OBJECT,
            paymentDevice: EMPTY_STRING,
            inventory: [...transactionObject.inventory],
            createdOn: transactionObject.createdOn,
          };

          setTransactionData(updatedTransactionData, currentStore?.id);
          customToast(message, toastType.SUCCESS, 7000);
        } else {
          // Payment failed or declined - reset payment state
          const failedTransactionData = {
            ...transactionObject,
            poiId: EMPTY_STRING,
            paymentDevice: EMPTY_STRING,
            paymentMethod: EMPTY_STRING,
            store: transactionObject.store,
            subTotal: transactionObject.SubTotal,
          };

          setTransactionData(failedTransactionData, currentStore?.id);
          customToast(message, toastType.ERROR, 7000);
        }
      },
    });
  });

  const toggleOrderDetailModal = () => {
    setIsShowShopifyOrderNotificationModal(
      !isShowShopifyOrderNotificationModal
    );
  };

  useEffect(() => {
    const prevLength = prevLengthRef.current;
    const currentLength = spinnerArray.length;

    if (prevLength === 0 && currentLength > 0) {
      loadingBarRef.current.continuousStart();
    } else if (currentLength === 0 && prevLength > 0) {
      loadingBarRef.current.complete();
    }

    // Update the previous length value
    prevLengthRef.current = currentLength;
  }, [spinnerArray]);

  return (
    <Fragment>
      <LoadingBar
        color="#6a18ed"
        ref={loadingBarRef}
        height={3}
        shadow={true}
      />
      {isDraftConfirmationModal ? <DraftConfirmationModal /> : ""}
      {isPrintLabelModal ? <ToastPrintLabelModal /> : ""}
      {isShowShopifyOrderNotificationModal ? (
        <OrderDetailsModal
          currentStore={currentStore}
          handleMarkOrderAsOpen={handleMarkOrderAsOpen}
          toggleOrderDetailModal={toggleOrderDetailModal}
          handleMarkOrderAsReady={handleMarkOrderAsReady}
          orderDetail={shopifyOrderNotificationModalDetails}
          updateShopifyOrderStatus={updateShopifyOrderStatus}
          defaultGridState={shopifyConstants.NOTIFICATION_MODAL}
          isShowOrderDetail={isShowShopifyOrderNotificationModal}
          handleMarkOrderAsFulfilled={handleMarkOrderAsFulfilled}
          handleTransferToLocation={handleTransferToLocation}
        />
      ) : (
        ""
      )}
      <div>
        <div>
          {isAuthenticated && <IdleUserModal />}
          {children}
        </div>
      </div>
    </Fragment>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
  isPrintLabelModal: state.printer.isPrintLabelModal,
  isAuthenticated: state.authentication.isAuthenticated,
  isDraftConfirmationModal: state.transaction.isDraftConfirmationModal,
  isShowShopifyOrderNotificationModal:
    state.shopify.isShowShopifyOrderNotificationModal,
  shopifyOrderNotificationModalDetails:
    state.shopify.shopifyOrderNotificationModalDetails,
  transaction: state.transaction,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getUserNotifications: (storeId) =>
    dispatch(storeActions.getUserNotifications(storeId)),
  setIsShowShopifyOrderNotificationModal: (value) =>
    dispatch(shopifyActions.setIsShowShopifyOrderNotificationModal(value)),
  setCurrentStore: (data) =>
    dispatch(success(data, storeConstants.SET_CURRENT_STORE)),
  updateAuditData: (data) =>
    dispatch(success(data, inventoryConstants.UPDATE_STOCK_AUDIT)),
  auditCompleted: (data) =>
    dispatch(success(data, storeConstants.UPDATE_AUDIT_SESSION_COMPLETE_FALSE)),
  updateShopifyOrderStatus: (
    storeId,
    data,
    gridState,
    toggleOrderDetailsModal
  ) =>
    dispatch(
      shopifyActions.updateShopifyOrderStatus(
        storeId,
        data,
        gridState,
        toggleOrderDetailsModal
      )
    ),
  resetShopifyStateToEmpty: () =>
    dispatch(shopifyActions.resetShopifyStateToEmpty()),
  setTransactionData: (data, storeId) =>
    dispatch(transactionActions.setTransactionData(data, storeId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MainWrapper);
